import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { createEngine, disposeEngine, parseModel, buildPart, applyTransform, fitAll, snapshotCamera, projectPoint, pickPoint, pickFace, faceRegion, computeMate, applyView, VIEW_DIRS, viewsFromFrontBottom, recordSequence, setSceneTheme, setFrontView, applyFusionNav, uid, renderExtras, enableViewCube, tiltView, setViewCubeFrame, snapPoint, circleFromFace, showSnapMarker, hideSnapMarker, showDimPreview, clearDimPreview, offsetFromCursor } from "@/lib/threeManual";
import { PartsTree } from "./PartsTree";
import { X, Plus, Trash2, Maximize, Loader2, Camera, MapPin, Move3d, ListOrdered, Save, ArrowUp, ArrowDown, MousePointer2, SquareDashedMousePointer, Link2, Check, Eye, EyeOff, Video, Undo2, Redo2, Sun, Moon, Home, ChevronUp, ChevronDown } from "lucide-react";
import { KannLoader } from "@/components/KannLoader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const zeroT = () => ({ px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0, visible: true });
const COLORS = ["#d9d9d9", "#DB960D", "#232323", "#C0392B", "#2E86DE", "#27AE60"];

const abToB64 = (buf) => {
  const bytes = new Uint8Array(buf); let bin = ""; const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
};

export default function InteractiveStudio({ manualId, initial, onClose, onSaved }) {
  const mountRef = useRef(null);
  const engineRef = useRef(null);
  const fileRef = useRef(null);
  const partsRef = useRef([]);
  const stepsRef = useRef([]);
  const activeRef = useRef(null);
  const modeRef = useRef("orbit");
  const hotspotRefs = useRef({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recMsg, setRecMsg] = useState("");
  const historyRef = useRef([]);
  const [histIdx, setHistIdx] = useState(-1);
  const histIdxRef = useRef(-1);
  const partDataRef = useRef({});
  const unsavedB64Ref = useRef({});
  const loadedRef = useRef(false);
  const dirtyRef = useRef(false);
  const persistRef = useRef(false);
  const recordingRef = useRef(false);
  const [autoSave, setAutoSave] = useState("idle");
  const [parts, setParts] = useState([]);
  const [steps, setSteps] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selPartId, setSelPartId] = useState(null);
  const [activeStepId, setActiveStepId] = useState(null);
  const [mode, setMode] = useState("orbit");
  const [selHot, setSelHot] = useState(null);
  const [viewNormals, setViewNormals] = useState({});
  const [theme, setThemeState] = useState(initial?.theme || "light");
  const [frontNormal, setFrontNormal] = useState(null);
  const setViewRef = useRef(null);
  const [dimKind, setDimKind] = useState("aligned");
  const dimP1 = useRef(null);
  const dimP2 = useRef(null);
  const lastOffset = useRef(null);
  const highlightRef = useRef([]);
  const downPos = useRef(null);
  const [alignStage, setAlignStage] = useState(0);
  const [alignMenu, setAlignMenu] = useState(false);
  const [alignChoice, setAlignChoice] = useState("center");
  const alignSrc = useRef(null);
  const alignTgt = useRef(null);
  const alignCenterA = useRef(null);
  const alignQuatA = useRef(null);
  const alignHi = useRef([]);

  useEffect(() => { partsRef.current = parts; }, [parts]);
  useEffect(() => { stepsRef.current = steps; }, [steps]);
  useEffect(() => { activeRef.current = activeStepId; }, [activeStepId]);
  useEffect(() => { modeRef.current = mode; const e = engineRef.current; if (e) { e.controls.enabled = !(mode === "hotspot" || mode === "dim" || mode === "dia"); if (mode !== "dim" && mode !== "dia") hideSnapMarker(e); if (mode !== "dim") { dimP1.current = null; dimP2.current = null; lastOffset.current = null; clearDimPreview(e); } } }, [mode]);

  const registerPart = (root, meta) => {
    const e = engineRef.current;
    const { outer, mat } = buildPart(root, meta.color);
    applyTransform(outer, meta.base);
    e.scene.add(outer);
    e.parts.push({ id: meta.id, outer, mat });
  };

  // init engine + load existing
  useEffect(() => {
    const engine = createEngine(mountRef.current, { theme: initial?.theme || "light", controls: "orbit" });
    engineRef.current = engine;
    if (typeof window !== "undefined") window.__eng = engine;
    applyFusionNav(engine);
    enableViewCube(engine, (dir) => setViewRef.current && setViewRef.current(dir));
    engine.onFrame = () => {
      const aid = activeRef.current; if (!aid) return;
      const s = stepsRef.current.find((x) => x.id === aid); if (!s) return;
      (s.hotspots || []).forEach((hs) => {
        const el = hotspotRefs.current[hs.id]; if (!el) return;
        const p = projectPoint(engine, hs.x, hs.y, hs.z);
        el.style.left = `${p.x}px`; el.style.top = `${p.y}px`; el.style.display = p.visible ? "block" : "none";
      });
    };
    (async () => {
      try {
        const initParts = initial?.parts || [];
        for (const meta of initParts) {
          const token = localStorage.getItem("kann_token");
          const r = await fetch(`${BACKEND}/api/manual-models/${meta.assetId}/raw`, { headers: { Authorization: `Bearer ${token}` } });
          const data = meta.ext === "obj" ? await r.text() : await r.arrayBuffer();
          partDataRef.current[meta.id] = data;
          registerPart(parseModel(meta.ext, data), meta);
        }
        setParts(initParts.map((m) => ({ id: m.id, assetId: m.assetId, name: m.name, ext: m.ext, color: m.color || "#d9d9d9", base: m.base || zeroT(), folderId: m.folderId || null })));
        setFolders(initial?.folders || []);
        setSteps((initial?.steps || []).map((s) => ({ ...s, transforms: s.transforms || {}, hotspots: s.hotspots || [], colors: s.colors || {}, hidden: s.hidden || {} })));
        setViewNormals(initial?.views || {});
        if (initParts.length) fitAll(engine, true);
        setSceneTheme(engine, initial?.theme || "light");
        setFrontView(engine, (initial?.views || {}).front);
      } catch (e) { toast.error("Kon bestaande onderdelen niet laden"); }
      finally { setLoading(false); }
    })();
    return () => { disposeEngine(engine); engineRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTransforms = () => {
    const aid = activeRef.current;
    if (aid) { const s = stepsRef.current.find((x) => x.id === aid); return s?.transforms || {}; }
    const m = {}; partsRef.current.forEach((p) => (m[p.id] = p.base)); return m;
  };

  // apply transforms + colors whenever state changes
  useEffect(() => {
    const e = engineRef.current; if (!e) return;
    const tmap = activeTransforms();
    const aid = activeRef.current;
    const stepColors = aid ? (steps.find((x) => x.id === aid)?.colors || {}) : {};
    const stepHidden = aid ? (steps.find((x) => x.id === aid)?.hidden || {}) : {};
    e.parts.forEach((ep) => {
      const meta = parts.find((p) => p.id === ep.id);
      ep.mat.color.set(stepColors[ep.id] || meta?.color || "#d9d9d9");
      const t = tmap[ep.id] || meta?.base || zeroT();
      const vis = aid ? !stepHidden[ep.id] : (meta?.base?.visible !== false);
      applyTransform(ep.outer, { ...t, visible: vis });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, steps, activeStepId]);

  // move camera to active step instantly
  useEffect(() => {
    const e = engineRef.current; if (!e || !activeStepId) return;
    const s = steps.find((x) => x.id === activeStepId);
    if (s?.cam) { e.camera.position.set(s.cam.px, s.cam.py, s.cam.pz); e.controls.target.set(s.cam.tx, s.cam.ty, s.cam.tz); e.controls.update(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepId]);

  // ---- history (undo/redo) + auto-save ----
  const clone = (o) => JSON.parse(JSON.stringify(o));
  useEffect(() => { histIdxRef.current = histIdx; }, [histIdx]);
  useEffect(() => { recordingRef.current = recording; }, [recording]);

  // seed the initial history entry once loading finishes
  useEffect(() => {
    if (loading || loadedRef.current) return;
    loadedRef.current = true;
    historyRef.current = [clone({ parts, steps, folders, views: viewNormals, theme })];
    setHistIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // push a debounced history snapshot on any meaningful change
  useEffect(() => {
    if (!loadedRef.current) return;
    const timer = setTimeout(() => {
      const snap = { parts, steps, folders, views: viewNormals, theme };
      const cur = historyRef.current[histIdxRef.current];
      if (cur && JSON.stringify(cur) === JSON.stringify(snap)) return;
      const next = historyRef.current.slice(0, histIdxRef.current + 1);
      next.push(clone(snap));
      while (next.length > 60) next.shift();
      historyRef.current = next;
      setHistIdx(next.length - 1);
      dirtyRef.current = true;
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, steps, folders, viewNormals, theme]);

  // debounced auto-save to the backend
  useEffect(() => {
    if (!loadedRef.current) return;
    const timer = setTimeout(() => { if (dirtyRef.current && !recordingRef.current && !persistRef.current) persist(true); }, 1600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, steps, folders, viewNormals, theme]);

  const syncMeshes = (snapParts) => {
    const e = engineRef.current; if (!e) return;
    const ids = snapParts.map((p) => p.id);
    e.parts.filter((ep) => !ids.includes(ep.id)).forEach((ep) => e.scene.remove(ep.outer));
    e.parts = e.parts.filter((ep) => ids.includes(ep.id));
    snapParts.forEach((p) => {
      if (!e.parts.find((ep) => ep.id === p.id)) {
        const d = partDataRef.current[p.id];
        if (d !== undefined) registerPart(parseModel(p.ext, d), p);
      }
    });
  };
  const restore = (snap) => {
    syncMeshes(snap.parts);
    setParts(clone(snap.parts));
    setSteps(clone(snap.steps));
    setFolders(clone(snap.folders || []));
    setViewNormals(clone(snap.views || {}));
    if (snap.theme) { setThemeState(snap.theme); setSceneTheme(engineRef.current, snap.theme); }
    dirtyRef.current = true;
    if (selPartId && !snap.parts.find((p) => p.id === selPartId)) setSelPartId(null);
  };
  const toggleTheme = () => {
    const nt = theme === "dark" ? "light" : "dark";
    setThemeState(nt);
    setSceneTheme(engineRef.current, nt);
  };
  const undo = () => {
    const i = histIdxRef.current; if (i <= 0) return;
    setHistIdx(i - 1); histIdxRef.current = i - 1;
    restore(historyRef.current[i - 1]);
  };
  const redo = () => {
    const i = histIdxRef.current; if (i >= historyRef.current.length - 1) return;
    setHistIdx(i + 1); histIdxRef.current = i + 1;
    restore(historyRef.current[i + 1]);
  };
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (k === "y" || (k === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
      else if (k === "escape") { setMode("orbit"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- parts ----
  const loadFile = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["stl", "obj"].includes(ext)) { toast.error("Alleen STL of OBJ"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const buf = reader.result;
        const b64 = abToB64(buf);
        const id = uid();
        const parsed = ext === "obj" ? new TextDecoder().decode(buf) : buf;
        const meta = { id, assetId: null, name: f.name.replace(/\.[^.]+$/, ""), ext, color: "#d9d9d9", base: zeroT() };
        partDataRef.current[id] = parsed;
        unsavedB64Ref.current[id] = b64;
        registerPart(parseModel(ext, parsed), meta);
        setParts((prev) => [...prev, meta]);
        setSelPartId(id);
        setTimeout(() => fitAll(engineRef.current, true), 30);
      } catch { toast.error("Kon model niet laden"); }
    };
    reader.readAsArrayBuffer(f);
  };
  const onUpload = (e) => { Array.from(e.target.files || []).forEach(loadFile); e.target.value = ""; };

  const removePart = (id) => {
    const e = engineRef.current;
    const idx = e.parts.findIndex((p) => p.id === id);
    if (idx >= 0) { e.scene.remove(e.parts[idx].outer); e.parts.splice(idx, 1); }
    setParts((prev) => prev.filter((p) => p.id !== id));
    setSteps((prev) => prev.map((s) => { const t = { ...s.transforms }; delete t[id]; return { ...s, transforms: t }; }));
    if (selPartId === id) setSelPartId(null);
  };

  const setPartField = (id, patch) => setParts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  // per-step visibility override (disable a part within a step)
  const isVisibleInStep = (id) => {
    if (!activeStepId) return true;
    return !(steps.find((x) => x.id === activeStepId)?.hidden?.[id]);
  };

  // base-level visibility (montage view, no active step)
  const isVisibleBase = (id) => parts.find((p) => p.id === id)?.base?.visible !== false;
  const setVisible = (ids, vis) => {
    if (activeStepId) {
      setSteps((prev) => prev.map((s) => {
        if (s.id !== activeStepId) return s;
        const hidden = { ...(s.hidden || {}) };
        ids.forEach((id) => { if (vis) delete hidden[id]; else hidden[id] = true; });
        return { ...s, hidden };
      }));
    } else setParts((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, base: { ...p.base, visible: vis } } : p)));
  };

  // color: base (no active step) or per-step highlight override
  const stepColorOf = (id) => steps.find((x) => x.id === activeStepId)?.colors?.[id];
  const currentColor = () => (activeStepId ? stepColorOf(selPartId) : parts.find((p) => p.id === selPartId)?.color);
  const setColor = (hex) => {
    if (!selPartId) return;
    if (activeStepId) {
      setSteps((prev) => prev.map((s) => {
        if (s.id !== activeStepId) return s;
        const colors = { ...(s.colors || {}) };
        if (hex === null) delete colors[selPartId]; else colors[selPartId] = hex;
        return { ...s, colors };
      }));
    } else setPartField(selPartId, { color: hex });
  };

  // set a transform axis in the active context (step or base)
  const setAxis = (axis, val) => {
    if (!selPartId) return;
    if (activeStepId) {
      setSteps((prev) => prev.map((s) => {
        if (s.id !== activeStepId) return s;
        const cur = s.transforms[selPartId] || { ...(parts.find((p) => p.id === selPartId)?.base || zeroT()) };
        return { ...s, transforms: { ...s.transforms, [selPartId]: { ...cur, [axis]: val } } };
      }));
    } else {
      setParts((prev) => prev.map((p) => (p.id === selPartId ? { ...p, base: { ...p.base, [axis]: val } } : p)));
    }
  };
  const curT = () => {
    if (!selPartId) return zeroT();
    if (activeStepId) { const s = steps.find((x) => x.id === activeStepId); return s?.transforms[selPartId] || parts.find((p) => p.id === selPartId)?.base || zeroT(); }
    return parts.find((p) => p.id === selPartId)?.base || zeroT();
  };

  // ---- steps ----
  const addStep = () => {
    if (!parts.length) { toast.error("Voeg eerst een 3D-onderdeel toe"); return; }
    const transforms = {}; partsRef.current.forEach((p) => (transforms[p.id] = { ...p.base, visible: true }));
    const s = { id: uid(), title: `Stap ${steps.length + 1}`, description: "", cam: snapshotCamera(engineRef.current), transforms, hotspots: [], colors: {}, hidden: {} };
    setSteps((prev) => [...prev, s]); setActiveStepId(s.id); setMode("orbit");
    toast.success("Stap toegevoegd — draai het model en leg de camera vast");
  };
  const setStepField = (id, patch) => setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const removeStep = (id) => { setSteps((prev) => prev.filter((s) => s.id !== id)); if (activeStepId === id) setActiveStepId(null); };
  const moveStep = (idx, dir) => setSteps((prev) => { const a = [...prev]; const j = idx + dir; if (j < 0 || j >= a.length) return prev; [a[idx], a[j]] = [a[j], a[idx]]; return a; });
  const captureCam = () => { if (!activeStepId) return; setStepField(activeStepId, { cam: snapshotCamera(engineRef.current) }); toast.success("Camerastand vastgelegd"); };

  // ---- hotspots ----
  const onCanvasClick = (ev) => {
    if (modeRef.current === "align") { handleAlignPick(ev); return; }
    if (modeRef.current === "face") { handleFacePick(ev); return; }
    if (modeRef.current === "dim") { handleDimPick(ev); return; }
    if (modeRef.current === "dia") { handleDiaPick(ev); return; }
    if (modeRef.current !== "hotspot" || !activeRef.current) return;
    const pt = pickPoint(engineRef.current, ev);
    if (!pt) { toast.info("Klik op het model om een infopunt te plaatsen"); return; }
    const id = uid();
    setSteps((prev) => prev.map((s) => (s.id === activeRef.current ? { ...s, hotspots: [...(s.hotspots || []), { id, x: pt.x, y: pt.y, z: pt.z, label: String((s.hotspots || []).length + 1), description: "" }] } : s)));
    setSelHot(id); setMode("orbit");
  };
  const updateHot = (id, patch) => setSteps((prev) => prev.map((s) => (s.id === activeStepId ? { ...s, hotspots: s.hotspots.map((h) => (h.id === id ? { ...h, ...patch } : h)) } : s)));
  const removeHot = (id) => { setSteps((prev) => prev.map((s) => (s.id === activeStepId ? { ...s, hotspots: s.hotspots.filter((h) => h.id !== id) } : s))); setSelHot(null); };

  // ---- indicators & mounting surfaces removed per user request ----


  // ---- CAD dimensions with snap points (per step) ----
  const dimColor = "#232323";
  const handleDimPick = (ev) => {
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return;
    if (!activeRef.current) { toast.info("Kies eerst een stap"); return; }
    // stage 3: label/offset already dragged → click places it and exits the tool
    if (dimP1.current && dimP2.current) {
      const d = { id: uid(), kind: dimKind, p1: dimP1.current, p2: dimP2.current, offset: lastOffset.current, color: dimColor };
      setSteps((prev) => prev.map((s) => (s.id === activeRef.current ? { ...s, dims: [...(s.dims || []), d] } : s)));
      dimP1.current = null; dimP2.current = null; lastOffset.current = null;
      hideSnapMarker(engineRef.current); clearDimPreview(engineRef.current);
      setMode("orbit");
      toast.success("Maat geplaatst");
      return;
    }
    const sn = snapPoint(engineRef.current, ev);
    if (!sn) { toast.info("Klik op het model bij een snappunt"); return; }
    if (!dimP1.current) { dimP1.current = sn.point; toast.info("Tweede punt aanklikken…"); return; }
    // stage 2: second point set → now drag the dimension line/label into position
    dimP2.current = sn.point;
    toast.info("Sleep de maatlijn naar de gewenste plek en klik om te plaatsen");
  };
  const handleDiaPick = (ev) => {
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return;
    if (!activeRef.current) { toast.info("Kies eerst een stap"); return; }
    const circ = circleFromFace(engineRef.current, ev);
    if (!circ || !circ.radius) { toast.info("Klik op een rond vlak (cirkel/boog)"); return; }
    const d = { id: uid(), kind: "dia", center: circ.center, radius: circ.radius, normal: circ.normal, color: dimColor };
    setSteps((prev) => prev.map((s) => (s.id === activeRef.current ? { ...s, dims: [...(s.dims || []), d] } : s)));
    hideSnapMarker(engineRef.current); setMode("orbit");
    toast.success("Diameter geplaatst");
  };
  const startDim = (kind) => {
    const target = kind === "dia" ? "dia" : "dim";
    if (modeRef.current === target && (target === "dia" || dimKind === kind)) { setMode("orbit"); return; }
    setDimKind(kind); dimP1.current = null; dimP2.current = null; lastOffset.current = null; clearDimPreview(engineRef.current); setMode(target);
    toast.info(kind === "dia" ? "Klik op een rond vlak" : "Klik 2 snappunten, sleep dan de maatlijn en klik om te plaatsen");
  };
  const removeDim = (id) => setSteps((prev) => prev.map((s) => (s.id === activeStepId ? { ...s, dims: (s.dims || []).filter((d) => d.id !== id) } : s)));
  const onCanvasMove = (ev) => {
    const m = modeRef.current;
    const e = engineRef.current; if (!e) return;
    if (m === "dim") {
      if (dimP1.current && dimP2.current) {
        const off = offsetFromCursor(e, ev, dimP1.current, dimP2.current, dimKind);
        lastOffset.current = off;
        hideSnapMarker(e);
        showDimPreview(e, { kind: dimKind, p1: dimP1.current, p2: dimP2.current, offset: off, color: dimColor });
      } else {
        const sn = snapPoint(e, ev);
        if (sn) {
          showSnapMarker(e, sn.point);
          if (dimP1.current) showDimPreview(e, { kind: dimKind, p1: dimP1.current, p2: sn.point, color: dimColor });
        } else { hideSnapMarker(e); clearDimPreview(e); }
      }
    }
    else if (m === "dia") { const c = circleFromFace(e, ev); if (c && c.radius) showSnapMarker(e, c.center); else hideSnapMarker(e); }
  };

  // keep the 3D dimensions in sync with the active step
  useEffect(() => {
    const e = engineRef.current; if (!e || loading) return;
    const s = steps.find((x) => x.id === activeStepId);
    renderExtras(e, (s && s.dims) || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, activeStepId, loading]);

  const setView = (dir) => {
    const e = engineRef.current; if (!e || !e.radius) return;
    applyView(e, viewNormals[dir] || VIEW_DIRS[dir] || VIEW_DIRS.iso);
  };
  useEffect(() => { setViewRef.current = setView; });
  useEffect(() => { const e = engineRef.current; if (e && e._gizmo) setViewCubeFrame(e, viewNormals); }, [viewNormals, loading]);

  const clearFace = () => {
    const e = engineRef.current;
    if (e) highlightRef.current.forEach((h) => e.scene.remove(h));
    highlightRef.current = []; setFrontNormal(null);
  };
  const handleFacePick = (ev) => {
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return; // ignore drag (orbit)
    const e = engineRef.current;
    const f = pickFace(e, ev);
    if (!f) { toast.info("Klik op een vlak van het model"); return; }
    e.scene.add(f.highlight); highlightRef.current.push(f.highlight);
    if (!frontNormal) {
      setFrontNormal(f.normal);
      applyView(e, f.normal);
      toast.info("Voorvlak gekozen — klik nu het ondervlak (onderkant) aan");
    } else {
      const views = viewsFromFrontBottom(frontNormal, f.normal);
      setViewNormals(views);
      setFrontView(e, views.front);
      applyView(e, views.front);
      clearFace(); setMode("orbit");
      toast.success("Aanzichten ingesteld op basis van voor- en ondervlak");
    }
  };

  // ---- face-to-face alignment (assemble parts) ----
  const setTransformForPart = (id, patch) => {
    if (activeStepId) {
      setSteps((prev) => prev.map((s) => {
        if (s.id !== activeStepId) return s;
        const cur = s.transforms[id] || { ...(parts.find((p) => p.id === id)?.base || zeroT()) };
        return { ...s, transforms: { ...s.transforms, [id]: { ...cur, ...patch } } };
      }));
    } else {
      setParts((prev) => prev.map((p) => (p.id === id ? { ...p, base: { ...p.base, ...patch } } : p)));
    }
  };
  const removeAlignHi = () => { const e = engineRef.current; if (e) alignHi.current.forEach((h) => e.scene.remove(h)); alignHi.current = []; };
  const clearAlign = () => { removeAlignHi(); alignSrc.current = null; alignTgt.current = null; alignCenterA.current = null; alignQuatA.current = null; setAlignStage(0); setAlignMenu(false); setAlignChoice("center"); };
  const applyMate = (align) => {
    const src = alignSrc.current, tgt = alignTgt.current;
    if (!src || !tgt) return;
    const res = computeMate(src, tgt, alignCenterA.current, alignQuatA.current, align);
    setTransformForPart(src.partId, { px: res.position[0], py: res.position[1], pz: res.position[2], rx: res.rotation[0], ry: res.rotation[1], rz: res.rotation[2] });
    setAlignChoice(align);
  };
  const handleAlignPick = (ev) => {
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return;
    const e = engineRef.current;
    const isTarget = !!alignSrc.current;
    const reg = faceRegion(e, ev, isTarget ? 0x27ae60 : 0x2e86de);
    if (!reg || !reg.partId) { toast.info("Klik op een vlak van een onderdeel"); return; }
    if (!isTarget) {
      const pA = e.parts.find((p) => p.id === reg.partId);
      alignSrc.current = reg;
      alignCenterA.current = pA.outer.position.clone();
      alignQuatA.current = pA.outer.quaternion.clone();
      e.scene.add(reg.highlight); alignHi.current.push(reg.highlight);
      setAlignStage(1);
      toast.info("Vlak van A gekozen — klik nu een vlak op een ANDER onderdeel");
    } else {
      if (reg.partId === alignSrc.current.partId) { toast.info("Kies een vlak op een ander onderdeel"); return; }
      alignTgt.current = reg;
      e.scene.add(reg.highlight); alignHi.current.push(reg.highlight);
      applyMate("center");
      setAlignMenu(true);
      setTimeout(removeAlignHi, 400);
      toast.success("Onderdelen gekoppeld — kies eventueel de uitlijning");
    }
  };

  const renderVideo = async () => {
    if (!steps.length) { toast.error("Voeg eerst stappen toe om een video te maken"); return; }
    const engine = engineRef.current; if (!engine || !engine.parts.length) return;
    setRecording(true); setRecMsg("Video voorbereiden…");
    try {
      const { blob, ext } = await recordSequence(engine, { parts, steps }, {
        transitionMs: 1800, holdMs: 1600, introMs: 1000, fps: 30,
        onProgress: (n, total) => setRecMsg(`Stap ${n} / ${total} opgenomen…`),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `interactieve-handleiding.${ext}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Video gedownload (.${ext})`);
    } catch (e) {
      toast.error(e?.message === "no-capture" ? "Video-opname wordt niet ondersteund in deze browser" : "Video renderen mislukt");
    } finally { setRecording(false); setRecMsg(""); }
  };

  const persist = async (silent) => {
    if (persistRef.current) return;
    persistRef.current = true;
    if (silent) setAutoSave("saving"); else setSaving(true);
    try {
      let changed = false;
      const outParts = [];
      for (const p of parts) {
        let assetId = p.assetId;
        const b64 = unsavedB64Ref.current[p.id];
        if (!assetId && b64) {
          const { data } = await api.post(`/manuals/${manualId}/models`, { name: p.name, ext: p.ext, data: b64 });
          assetId = data.id; delete unsavedB64Ref.current[p.id]; changed = true;
        }
        outParts.push({ id: p.id, assetId, name: p.name, ext: p.ext, color: p.color, base: p.base, folderId: p.folderId || null });
      }
      const config = { enabled: outParts.length > 0 && steps.length > 0, parts: outParts, steps, folders, views: viewNormals, theme };
      await api.put(`/manuals/${manualId}/interactive`, { interactive: config });
      if (changed) setParts((prev) => prev.map((pp) => { const op = outParts.find((o) => o.id === pp.id); return op ? { ...pp, assetId: op.assetId } : pp; }));
      onSaved && onSaved(config);
      dirtyRef.current = false;
      if (silent) setAutoSave("saved"); else toast.success("Interactieve handleiding opgeslagen & gepubliceerd");
    } catch (e) {
      if (silent) setAutoSave("idle"); else toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally { persistRef.current = false; if (!silent) setSaving(false); }
  };
  const save = () => persist(false);

  const activeStep = steps.find((s) => s.id === activeStepId);
  const selHotObj = activeStep?.hotspots?.find((h) => h.id === selHot);
  const posMax = (engineRef.current?.radius || 50) * 2, posStep = 0.1;
  const t = curT();

  const setAxisFine = (axis, val) => setAxis(axis, Math.round(val * 10) / 10);
  const slider = (label, axis, min, max, step, unit = "") => {
    const val = Math.round((t[axis] || 0) * 10) / 10;
    return (
      <div className="flex items-center gap-1">
        <span className="text-[11px] w-3 shrink-0 text-kann-text-sec">{label}</span>
        <input type="range" min={min} max={max} step={step} value={t[axis] || 0} onChange={(e) => setAxis(axis, Number(e.target.value))} onDoubleClick={() => setAxis(axis, 0)} title="Dubbelklik om te centreren (0)" data-testid={`is-axis-${axis}`} className="flex-1 min-w-0 accent-kann-gold" />
        <button onClick={() => setAxisFine(axis, (t[axis] || 0) - step)} data-testid={`is-axis-dec-${axis}`} title={`-${step}${unit}`} className="w-5 h-5 shrink-0 rounded border border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active leading-none flex items-center justify-center text-sm">−</button>
        <input type="number" step={step} value={val} onChange={(e) => setAxisFine(axis, Number(e.target.value) || 0)} data-testid={`is-axis-val-${axis}`} title={`Exacte waarde (${step}${unit} per stap)`} className="w-12 shrink-0 bg-kann-base border border-kann-border/40 rounded px-1 py-0.5 text-[10px] tabular-nums text-right text-kann-text-pri outline-none focus:border-kann-gold" />
        <button onClick={() => setAxisFine(axis, (t[axis] || 0) + step)} data-testid={`is-axis-inc-${axis}`} title={`+${step}${unit}`} className="w-5 h-5 shrink-0 rounded border border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active leading-none flex items-center justify-center text-sm">+</button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-kann-base flex flex-col" data-testid="interactive-studio">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-kann-border/20 shrink-0 bg-kann-surface">
        <Move3d className="w-5 h-5 text-kann-gold" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-kann-text-pri">Interactieve 3D-handleiding</div>
          <div className="text-xs text-kann-text-sec">Upload onderdelen, leg per stap een camerastand + exploded view vast en plaats infopunten.</div>
        </div>
        <div className="flex items-center gap-1.5 mr-1 text-xs text-kann-text-sec" data-testid="is-autosave-status">
          {autoSave === "saving" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opslaan…</> : autoSave === "saved" ? <><Check className="w-3.5 h-3.5 text-green-500" /> Automatisch opgeslagen</> : null}
        </div>
        <button onClick={toggleTheme} data-testid="is-theme-toggle" title={theme === "dark" ? "Licht thema" : "Donker thema (lichter model)"} className="p-2 rounded-md text-kann-text-sec hover:bg-kann-surface-active" >{theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={histIdx <= 0} data-testid="is-undo-btn" title="Ongedaan maken (Ctrl+Z)" className="p-2 rounded-md text-kann-text-sec hover:bg-kann-surface-active disabled:opacity-30"><Undo2 className="w-4 h-4" /></button>
          <button onClick={redo} disabled={histIdx >= historyRef.current.length - 1} data-testid="is-redo-btn" title="Opnieuw (Ctrl+Shift+Z)" className="p-2 rounded-md text-kann-text-sec hover:bg-kann-surface-active disabled:opacity-30"><Redo2 className="w-4 h-4" /></button>
        </div>
        <button onClick={renderVideo} disabled={recording || saving || steps.length === 0} data-testid="is-render-video-btn" title="Speel de volledige sequentie af en download als video" className="border border-kann-gold text-kann-gold font-semibold rounded-full px-4 py-2 text-sm hover:bg-kann-gold/10 flex items-center gap-2 disabled:opacity-50">
          {recording ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} {recording ? "Renderen…" : "Video renderen"}
        </button>
        <button onClick={save} disabled={saving} data-testid="is-save-btn" className="bg-kann-ink text-white font-semibold rounded-full px-5 py-2 text-sm hover:bg-black flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Opslaan &amp; publiceren
        </button>
        <button onClick={onClose} data-testid="is-close-btn" className="p-2 rounded-md hover:bg-kann-surface-active text-kann-text-sec"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left: parts + transforms */}
        <div className="w-64 shrink-0 border-r border-kann-border/20 flex flex-col bg-kann-surface">
          <div className="p-3 border-b border-kann-border/20 flex gap-2">
            <button onClick={() => fileRef.current?.click()} data-testid="is-add-part-btn" className="flex-1 bg-kann-ink text-white text-sm font-semibold rounded-full px-3 py-2 hover:bg-black flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Onderdeel</button>
            <button onClick={() => fitAll(engineRef.current, true)} data-testid="is-fit-btn" title="Passend maken" className="border border-kann-border/50 rounded-full px-3 py-2 text-kann-text-sec hover:bg-kann-surface-active"><Maximize className="w-4 h-4" /></button>
            <input ref={fileRef} type="file" multiple accept=".stl,.obj" className="hidden" onChange={onUpload} data-testid="is-part-file-input" />
          </div>
          <PartsTree parts={parts} folders={folders} setParts={setParts} setFolders={setFolders} selPartId={selPartId} setSelPartId={setSelPartId}
            isVisible={(id) => (activeStepId ? isVisibleInStep(id) : isVisibleBase(id))} setVisible={setVisible} removePart={removePart} setPartField={setPartField} />
          {selPartId && (
            <div className="p-3 border-t border-kann-border/20 space-y-2" data-testid="is-part-controls">
              <div className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono">{activeStepId ? "Positie in deze stap" : "Basispositie (montage)"}</div>
              <div className="pb-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] text-kann-text-sec mr-0.5">{activeStepId ? "Stapkleur" : "Kleur"}</span>
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => setColor(c)} data-testid={`is-color-${c}`} className={`w-5 h-5 rounded-sm border ${(currentColor() || "").toLowerCase() === c.toLowerCase() ? "ring-2 ring-kann-gold border-kann-gold" : "border-kann-border/50"}`} style={{ background: c }} />
                  ))}
                  {activeStepId && <button onClick={() => setColor(null)} data-testid="is-stepcolor-reset" title="Basiskleur gebruiken" className={`text-[10px] px-1.5 py-0.5 rounded border ${!currentColor() ? "ring-2 ring-kann-gold border-kann-gold text-kann-text-pri" : "border-kann-border/50 text-kann-text-sec"} hover:bg-kann-surface-active`}>Basis</button>}
                </div>
                {activeStepId && <div className="text-[10px] text-kann-text-sec mt-1 leading-snug">Markeer het te monteren onderdeel. Laat op "Basis" in latere stappen zodat het terugkeert naar de oorspronkelijke kleur.</div>}
              </div>
              {slider("X", "px", -posMax, posMax, posStep)}
              {slider("Y", "py", -posMax, posMax, posStep)}
              {slider("Z", "pz", -posMax, posMax, posStep)}
              <div className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono pt-1">Rotatie</div>
              {slider("X", "rx", -180, 180, 0.1, "°")}
              {slider("Y", "ry", -180, 180, 0.1, "°")}
              {slider("Z", "rz", -180, 180, 0.1, "°")}
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="relative flex-1 bg-white">
          <div ref={mountRef} onClick={onCanvasClick} onPointerMove={onCanvasMove} onPointerDown={(e) => (downPos.current = { x: e.clientX, y: e.clientY })} className="w-full h-full" data-testid="is-canvas" style={{ backgroundImage: "radial-gradient(#E5E5E5 1px, transparent 1px)", backgroundSize: "20px 20px", cursor: mode === "orbit" ? "default" : "crosshair" }} />
          {parts.length > 0 && !recording && !loading && (
            <div className="absolute bottom-3 left-3 flex items-center gap-3 rounded-lg bg-kann-surface/85 backdrop-blur-sm border border-kann-border/30 px-3 py-1.5 text-[10px] text-kann-text-sec pointer-events-none" data-testid="is-nav-hint">
              <span className="uppercase tracking-wider text-kann-gold font-mono">Fusion</span>
              <span>Midden: <b className="text-kann-text-pri">pannen</b></span>
              <span>Shift+midden: <b className="text-kann-text-pri">draaien</b></span>
              <span>Scroll: <b className="text-kann-text-pri">zoomen</b></span>
            </div>
          )}
          {loading && <KannLoader theme={theme} label="Onderdelen laden…" />}
          {recording && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 backdrop-blur-[2px] z-40 pointer-events-auto" data-testid="is-recording-overlay">
              <div className="bg-kann-surface rounded-2xl px-7 py-5 flex flex-col items-center gap-2 shadow-xl">
                <div className="flex items-center gap-2 text-kann-gold"><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> <Video className="w-5 h-5" /></div>
                <div className="text-sm font-semibold text-kann-text-pri">Video wordt opgenomen…</div>
                <div className="text-xs text-kann-text-sec tabular-nums" data-testid="is-recording-msg">{recMsg}</div>
                <div className="text-[10px] text-kann-text-sec mt-1">Laat dit tabblad open staan tot het downloaden start.</div>
              </div>
            </div>
          )}
          {!loading && parts.length === 0 && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="text-kann-text-sec text-sm">Klik op "Onderdeel" om een STL/OBJ te uploaden</div></div>}
          {parts.length > 0 && (
            <div className="absolute top-3 left-3 flex gap-1.5 z-20" data-testid="is-view-tools">
              <button onClick={() => { setMode(mode === "face" ? "orbit" : "face"); clearFace(); }} data-testid="is-face-mode" title="Selecteer een vlak en koppel het aan een aanzicht" className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors border ${mode === "face" ? "bg-kann-gold text-kann-ink border-kann-gold font-semibold" : "bg-black/70 text-white border-transparent hover:bg-kann-gold hover:text-kann-ink"}`}><SquareDashedMousePointer className="w-3.5 h-3.5" /> Vlak</button>
              <button onClick={() => { const on = mode !== "align"; setMode(on ? "align" : "orbit"); clearAlign(); }} data-testid="is-align-mode" title="Koppel een vlak van het ene onderdeel tegen een vlak van het andere" className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors border ${mode === "align" ? "bg-kann-gold text-kann-ink border-kann-gold font-semibold" : "bg-black/70 text-white border-transparent hover:bg-kann-gold hover:text-kann-ink"}`}><Link2 className="w-3.5 h-3.5" /> Koppel</button>
            </div>
          )}
          {parts.length > 0 && (
            <div className="absolute top-3 flex flex-col gap-1.5 z-20" style={{ right: "132px" }} data-testid="is-viewcube-tools">
              <button onClick={() => setView("iso")} data-testid="is-view-iso" title="Iso (home)" className="w-8 h-8 rounded-full bg-black/70 text-white hover:bg-kann-gold hover:text-kann-ink flex items-center justify-center transition-colors"><Home className="w-4 h-4" /></button>
              <button onClick={() => tiltView(engineRef.current, 22.5)} data-testid="is-view-tilt-up" title="Kantel omhoog (22,5° per stap)" className="w-8 h-8 rounded-full bg-black/70 text-white hover:bg-kann-gold hover:text-kann-ink flex items-center justify-center transition-colors"><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => tiltView(engineRef.current, -22.5)} data-testid="is-view-tilt-down" title="Kantel omlaag (22,5° per stap)" className="w-8 h-8 rounded-full bg-black/70 text-white hover:bg-kann-gold hover:text-kann-ink flex items-center justify-center transition-colors"><ChevronDown className="w-4 h-4" /></button>
            </div>
          )}
          {mode === "align" && !alignMenu && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#2E86DE] text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 z-30" data-testid="is-align-hint">
              {alignStage === 0 ? "Klik een vlak op onderdeel A (dit onderdeel verplaatst)" : "Klik nu een vlak op onderdeel B (blijft staan)"}
              <button onClick={() => { clearAlign(); setMode("orbit"); }} data-testid="is-align-cancel" className="hover:text-red-200"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {alignMenu && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-full px-2 py-1.5 flex items-center gap-1 z-30" data-testid="is-align-menu">
              <span className="text-xs text-kann-text-sec px-2">Uitlijnen:</span>
              {[["center", "Midden"], ["left", "Links"], ["right", "Rechts"], ["top", "Boven"], ["bottom", "Onder"]].map(([v, l]) => (
                <button key={v} onClick={() => applyMate(v)} data-testid={`is-align-${v}`} className={`text-xs px-2.5 py-1 rounded-full transition-colors ${alignChoice === v ? "bg-kann-gold text-kann-ink font-semibold" : "bg-kann-ink text-white hover:bg-kann-gold hover:text-kann-ink"}`}>{l}</button>
              ))}
              <button onClick={() => { clearAlign(); setMode("orbit"); }} data-testid="is-align-done" className="text-kann-ink hover:text-kann-gold px-1.5 flex items-center gap-1 text-xs font-semibold"><Check className="w-4 h-4" /> Klaar</button>
            </div>
          )}
          {mode === "face" && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-kann-gold text-kann-ink text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 z-30" data-testid="is-face-hint">
              {frontNormal ? "Stap 2/2 — klik het ONDERVLAK (onderkant) aan" : "Stap 1/2 — klik het VOORVLAK aan"}
              <button onClick={() => { clearFace(); setMode("orbit"); }} data-testid="is-face-cancel" className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {mode === "hotspot" && <div className="absolute top-14 left-3 bg-kann-gold text-kann-ink text-xs font-semibold px-3 py-1.5 rounded-full" data-testid="is-hotspot-hint">Klik op het model om een infopunt te plaatsen</div>}

          {/* hotspot markers for active step */}
          {activeStep && (activeStep.hotspots || []).map((hs) => (
            <div key={hs.id} ref={(el) => (hotspotRefs.current[hs.id] = el)} className="absolute z-10" style={{ transform: "translate(-50%,-50%)" }} data-testid={`is-hotspot-${hs.id}`}>
              <button onClick={(e) => { e.stopPropagation(); setSelHot(hs.id); }} className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shadow-md ${selHot === hs.id ? "bg-kann-gold text-white ring-2 ring-white" : "bg-white text-kann-ink ring-2 ring-kann-gold"}`}>{String(hs.label || "i").slice(0, 2)}</button>
            </div>
          ))}
          {selHotObj && (
            <div className="absolute bottom-3 left-3 bg-white border border-kann-border/30 rounded-lg shadow-lg p-3 w-72 space-y-2 z-20" data-testid="is-hotspot-editor">
              <div className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono">Infopunt</div>
              <input value={selHotObj.label} onChange={(e) => updateHot(selHot, { label: e.target.value })} data-testid="is-hotspot-label" placeholder="Label (bijv. A of 1)" className="w-full border border-kann-border/40 rounded px-2 py-1.5 text-sm outline-none focus:border-kann-gold" />
              <textarea value={selHotObj.description} onChange={(e) => updateHot(selHot, { description: e.target.value })} data-testid="is-hotspot-desc" rows={2} placeholder="Uitleg bij dit punt…" className="w-full border border-kann-border/40 rounded px-2 py-1.5 text-sm outline-none focus:border-kann-gold resize-none" />
              <div className="flex justify-between">
                <button onClick={() => setSelHot(null)} className="text-xs text-kann-text-sec hover:text-kann-text-pri">Sluiten</button>
                <button onClick={() => removeHot(selHot)} data-testid="is-hotspot-remove" className="text-xs text-red-500 hover:underline">Verwijderen</button>
              </div>
            </div>
          )}
        </div>

        {/* Right: steps */}
        <div className="w-72 shrink-0 border-l border-kann-border/20 flex flex-col bg-kann-surface">
          <div className="p-3 border-b border-kann-border/20 flex items-center justify-between">
            <span className="text-sm font-semibold text-kann-text-pri flex items-center gap-2"><ListOrdered className="w-4 h-4 text-kann-gold" /> Stappen</span>
            <button onClick={addStep} data-testid="is-add-step-btn" className="text-xs text-kann-gold hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Stap</button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="is-steps-list">
            {steps.length === 0 && <p className="text-xs text-kann-text-sec p-2">Nog geen stappen. Voeg een stap toe, draai het model naar de gewenste hoek en leg de camera vast.</p>}
            {steps.map((s, i) => (
              <div key={s.id} data-testid={`is-step-${i}`} className={`rounded-lg border p-2.5 ${activeStepId === s.id ? "border-kann-gold bg-kann-gold/10" : "border-kann-border/40 bg-kann-base/40"}`}>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setActiveStepId(activeStepId === s.id ? null : s.id)} data-testid={`is-step-select-${i}`} className={`shrink-0 w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center ${activeStepId === s.id ? "bg-kann-gold text-white" : "bg-kann-ink text-white"}`}>{String(i + 1).padStart(2, "0")}</button>
                  <input value={s.title} onChange={(e) => setStepField(s.id, { title: e.target.value })} data-testid={`is-step-title-${i}`} className="flex-1 min-w-0 bg-transparent text-sm font-medium text-kann-text-pri outline-none" placeholder="Titel" />
                  <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="p-1 text-kann-text-sec hover:text-kann-text-pri disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} className="p-1 text-kann-text-sec hover:text-kann-text-pri disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeStep(s.id)} data-testid={`is-step-del-${i}`} className="p-1 text-kann-text-sec hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                {activeStepId === s.id && (
                  <div className="mt-2 space-y-2">
                    <textarea value={s.description} onChange={(e) => setStepField(s.id, { description: e.target.value })} data-testid={`is-step-desc-${i}`} rows={2} placeholder="Beschrijving voor de klant…" className="w-full border border-kann-border/40 rounded px-2 py-1.5 text-xs outline-none focus:border-kann-gold resize-none bg-kann-base" />
                    <div className="flex gap-1.5">
                      <button onClick={captureCam} data-testid="is-capture-cam" className="flex-1 border border-kann-border/50 rounded-full px-2 py-1.5 text-xs text-kann-text-sec hover:bg-kann-surface-active flex items-center justify-center gap-1"><Camera className="w-3.5 h-3.5" /> Camera vastleggen</button>
                      <button onClick={() => setMode(mode === "hotspot" ? "orbit" : "hotspot")} data-testid="is-hotspot-mode" className={`flex-1 rounded-full px-2 py-1.5 text-xs flex items-center justify-center gap-1 border ${mode === "hotspot" ? "bg-kann-gold text-kann-ink border-kann-gold" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}><MapPin className="w-3.5 h-3.5" /> Infopunt</button>
                    </div>
                    <p className="text-[10px] text-kann-text-sec leading-snug flex items-start gap-1"><MousePointer2 className="w-3 h-3 mt-0.5 shrink-0" /> Draai/verplaats onderdelen voor deze stap, leg dan de camera vast. Infopunten horen bij deze stap.</p>

                    <div className="pt-1.5 border-t border-kann-border/30">
                      <div className="text-[10px] uppercase tracking-wider text-kann-text-sec font-mono mb-1 flex items-center gap-1"><Move3d className="w-3 h-3" /> Maatvoering (snap, mm)</div>
                      <div className="grid grid-cols-5 gap-1">
                        {[["aligned", "Afstand"], ["x", "X"], ["y", "Y"], ["z", "Z"], ["dia", "\u2300"]].map(([k, lbl]) => (
                          <button key={k} onClick={() => startDim(k)} data-testid={`is-dim-add-${k}`} title={k === "dia" ? "Diameter van rond vlak" : (k === "aligned" ? "Rechte afstand tussen 2 snappunten" : `Maat langs ${k.toUpperCase()}-as`)}
                            className={`py-1.5 rounded-md border text-[10px] transition-colors ${((mode === "dim" && dimKind === k) || (mode === "dia" && k === "dia")) ? "bg-kann-ink text-white border-kann-ink" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {(s.dims || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid="is-dim-list">
                          {(s.dims || []).map((d, di) => (
                            <span key={d.id} className="text-[10px] px-1.5 py-0.5 rounded-full border border-kann-border/50 text-kann-text-sec flex items-center gap-1">
                              {({ aligned: "Afstand", x: "X", y: "Y", z: "Z", dia: "\u2300" }[d.kind] || "Maat")} {di + 1}
                              <button onClick={() => removeDim(d.id)} data-testid={`is-dim-del-${d.id}`} className="text-red-500 hover:text-red-600">×</button>
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-kann-text-sec mt-1 leading-snug">Snapt op hoekpunten, randmiddens en vlak-/cirkelmidden. Klik 2 snappunten (of een rond vlak voor \u2300).</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
